<template>
  <v-form ref="form">
    <FormPanels>
      <FormPanel label="Redirect Info">
        <FormRow
          required
          label="Name"
          tooltip="An internal label to help you identify this redirect. Not visible to guests."
        >
          <TextField
            :rules="[validation.required]"
            placeholder="e.g. Old Landing Page"
            v-model="form.title"
          ></TextField>
        </FormRow>

        <FormRow
          required
          label="From URL"
          tooltip="The path on your Commons domain that should redirect. Leave blank to redirect the root of your site."
        >
          <v-layout align-center>
            <span class="mr-3">
              <div v-if="currentUser.active_project_env == 'production'">
                {{ currentUser.production_domain }}/
              </div>
              <div v-else>{{ currentUser.staging_domain }}/</div>
            </span>
            <div class="flex-grow-1">
              <TextField
                :rules="[validation.required]"
                placeholder="old-page"
                v-model="form.url"
              />
            </div>
          </v-layout>
        </FormRow>

        <FormRow
          required
          label="Destination URL"
          tooltip="Where visitors will be sent when they visit the From URL. Include https:// for external links, or a relative path for pages on your Commons domain."
        >
          <TextField
            :rules="[validation.required]"
            placeholder="https://example.com"
            v-model="form.absolute_url"
          ></TextField>
          <div class="inherited-hint inherited-hint--warning" v-if="sameDomainWarning">
            <v-icon size="14" color="#c00">mdi-alert-circle-outline</v-icon>
            <span
              >This points to your own Commons site’s
              {{ sameDomainWarning.envLabel }} domain
              (&ldquo;{{ sameDomainWarning.domain }}&rdquo;). Using a relative
              path instead keeps this redirect working no matter which
              environment or domain it's viewed from.</span
            >
            <button
              type="button"
              class="inherited-hint__action"
              @click="useRelativeDestination"
            >
              Use relative path instead
            </button>
          </div>
        </FormRow>
      </FormPanel>
    </FormPanels>

    <SubmitFooter :refs="$refs" :submit="submit"
      >{{ btnText }}
      <template v-if="form.id" #actions>
        <div>
          <v-btn
            @click="$emit('destroy')"
            outlined
            color="primary"
            elevation="0"
            rounded
            large
          >
            Delete
          </v-btn>
        </div>
      </template>
    </SubmitFooter>
  </v-form>
</template>

<script>
import mixin from "../_extends/form.extends.js";
export default {
  extends: mixin,
  computed: {
    sameDomainWarning() {
      const destination = (this.form.absolute_url || "").trim();
      if (!destination) return null;

      let parsed;
      try {
        parsed = new URL(destination);
      } catch (e) {
        // Not a parseable absolute URL (e.g. already relative) - nothing to warn about.
        return null;
      }

      const host = parsed.hostname.toLowerCase();
      const normalize = (domain) => (domain || "").toLowerCase().trim();
      const domains = this.currentUser.active_project.domains || {};

      if (host === normalize(domains.production)) {
        return {
          envLabel: "production",
          domain: domains.production,
          relativePath: this.relativePathFrom(parsed),
        };
      }

      if (host === normalize(domains.staging)) {
        return {
          envLabel: "staging",
          domain: domains.staging,
          relativePath: this.relativePathFrom(parsed),
        };
      }

      return null;
    },
  },
  methods: {
    relativePathFrom(parsedUrl) {
      const path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      return path.startsWith("/") ? path : `/${path}`;
    },
    useRelativeDestination() {
      if (!this.sameDomainWarning) return;
      this.form.absolute_url = this.sameDomainWarning.relativePath;
    },
  },
};
</script>

<style scoped lang="scss">
.inherited-hint {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #757575;

  .v-icon {
    margin-top: 1px;
  }

  &__action {
    flex-shrink: 0;
    color: var(--v-primary-base);
    font-weight: bold;
    text-decoration: underline;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    font-size: inherit;
  }

  &--warning {
    color: #c00;
  }
}
</style>
